import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { requestAPI } from '../utils/api';
import { pixelEvents } from '../utils/pixelEvents';
import SEO from '../components/SEO';
import TrustBadges from '../components/TrustBadges';
import './CustomRequest.css';

const CustomRequest = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    budget: '',
    timeline: ''
  });
  const [referenceImage, setReferenceImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setReferenceImage(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      data.append(key, formData[key]);
    });
    if (referenceImage) {
      data.append('reference_image', referenceImage);
    }

    try {
      await requestAPI.create(data);
      pixelEvents.customRequestSubmit(formData.name, formData.budget);
      setMessage({ 
        type: 'success', 
        text: 'Your custom request has been submitted! We will contact you soon.' 
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: '',
        budget: '',
        timeline: ''
      });
      setReferenceImage(null);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Failed to submit request. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const customRequestSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Custom Artwork Commission",
    "provider": {
      "@type": "Person",
      "name": "Aanishka Chowdhury"
    },
    "description": "Commission a custom painting tailored to your preferences and vision",
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "offers": {
      "@type": "Offer",
      "description": "Custom canvas painting commissions"
    }
  };

  return (
    <div className="custom-request-page">
      <SEO 
        title="Request Custom Artwork"
        description="Commission a custom painting tailored to your vision. Abstract art, portraits, landscapes, and personalized canvas artwork by Aanishka Chowdhury."
        keywords="custom painting, commission artwork, personalized art, custom canvas, bespoke painting, art commission India"
        url="/custom-request"
        schema={customRequestSchema}
      />
      
      <header className="request-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Request Custom Artwork
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Tell us about your vision and let's create something unique together
          </motion.p>
        </div>
      </header>

      <section className="container" aria-label="Custom artwork request form">
        <div className="request-content">
          <motion.aside
            className="request-info"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            aria-label="How custom requests work"
          >
            <h2>How It Works</h2>
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Submit Your Request</h3>
                <p>Fill out the form with your requirements and vision</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>We'll Contact You</h3>
                <p>Our artist will reach out to discuss details and timeline</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Creation Process</h3>
                <p>Watch your vision come to life with regular updates</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Delivery</h3>
                <p>Receive your unique custom artwork with Certificate of Authenticity</p>
              </div>
            </div>

            <div className="request-trust">
              <TrustBadges layout="vertical" />
            </div>
          </motion.aside>

          <motion.form
            className="request-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            aria-label="Custom artwork commission form"
          >
            {message.text && (
              <div className={`message ${message.type}`} role="alert">
                {message.text}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="name">Your Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
                aria-required="true"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
                aria-required="true"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 XXXXXXXXXX"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Project Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="5"
                placeholder="Describe your vision, preferred style, colors, size, and any specific details..."
                aria-required="true"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="budget">Budget Range</label>
                <input
                  type="text"
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  placeholder="e.g., ₹500-₹1000"
                />
              </div>

              <div className="form-group">
                <label htmlFor="timeline">Preferred Timeline</label>
                <input
                  type="text"
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  placeholder="e.g., 2-3 weeks"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="referenceImage">Reference Image (Optional)</label>
              <input
                type="file"
                id="referenceImage"
                onChange={handleFileChange}
                accept="image/*"
                className="file-input"
                aria-describedby="file-help"
              />
              <p id="file-help" className="file-note">Upload any reference images for inspiration</p>
            </div>

            <button type="submit" className="btn-primary" disabled={loading} aria-label="Submit custom artwork request">
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default CustomRequest;
